import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStore } from '@/shared/store/store-context';

import { KpiGrid } from '../components/kpi-grid';
import { RangePicker } from '../components/range-picker';
import { RollupBar } from '../components/rollup-bar';
import { SalesChart } from '../components/sales-chart';
import { TopProductsCard } from '../components/top-products-card';
import { useDayFormat } from '../hooks/use-day-format';
import { useOverviewReport } from '../hooks/use-overview-report';
import { useReportRange } from '../hooks/use-report-range';
import { previousRange } from '../schemas/report-range';

const KPIS = [
  'gross',
  'orders',
  'aov',
  'net',
  'units',
  'customers',
  'sessions',
  'conversionRate',
] as const;

function OverviewReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const days = useDayFormat();
  const { range, setRange } = useReportRange();
  const query = useOverviewReport(store.id, range);
  const report = query.data;
  const previous = previousRange(range);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 lg:flex-row lg:items-start lg:justify-between">
        <RangePicker range={range} onChange={setRange} />
        <RollupBar storeId={store.id} range={range} lastRollupAt={report?.lastRollupAt} />
      </div>
      <p className="text-muted-foreground -mt-3 text-xs">
        {t('overview.comparedWith', { range: days.range(previous.from, previous.to) })}
      </p>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <KpiGrid
            kpis={KPIS}
            className="lg:grid-cols-4"
            tour="reports.overview.kpis"
            totals={report?.totals}
            previous={report?.previous}
            isLoading={query.isPending}
          />
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2" data-tour="reports.overview.chart">
              <CardHeader>
                <CardTitle className="text-[15px]">{t('overview.daily')}</CardTitle>
              </CardHeader>
              <CardContent>
                {report ? (
                  <SalesChart
                    currency={report.currency}
                    summary={t('overview.chartSummary', {
                      range: days.range(report.from, report.to),
                      gross: format.money(report.totals.gross),
                      orders: report.totals.ordersCount,
                    })}
                    points={report.series.map((day) => ({
                      label: days.short(day.date),
                      gross: day.gross,
                      ordersCount: day.ordersCount,
                    }))}
                  />
                ) : (
                  <Skeleton className="h-[280px] w-full" />
                )}
              </CardContent>
            </Card>
            <TopProductsCard
              items={report?.topProducts}
              isLoading={query.isPending}
              viewAll={
                <Link
                  to="/s/$storeId/reports/products"
                  params={{ storeId: store.id }}
                  search={(prev) => prev}
                  className="text-primary text-[13px] hover:underline"
                >
                  {t('overview.viewAll')}
                </Link>
              }
            />
          </div>
        </>
      )}
    </div>
  );
}

export function OverviewReportPage() {
  return (
    <FeatureErrorBoundary name="reports.overview">
      <OverviewReport />
    </FeatureErrorBoundary>
  );
}
